// ═══════════════════════════════════════════════════════════════
// bookApi.js — Client /api/books (S56 P3)
// Module COM/Écrivain Foundation — livres + chapitres + versions.
// ═══════════════════════════════════════════════════════════════

const getToken = () => localStorage.getItem('oracle_token');

export class BookApiError extends Error {
    constructor(status, message, detail) {
        super(message || 'Erreur API books');
        this.name = 'BookApiError';
        this.status = status;
        this.detail = detail;
    }
}

async function request(path, { method = 'GET', body = null, timeoutMs = 30000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new BookApiError(res.status, data.error || data.message || `HTTP ${res.status}`, data);
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

// ─── Books ──────────────────────────────────────────────────────────
export function createBook(payload) {
    return request('/api/books', { method: 'POST', body: payload });
}
export function getBook(bookId) {
    return request(`/api/books/${bookId}`);
}
export function updateBook(bookId, updates) {
    return request(`/api/books/${bookId}`, { method: 'PUT', body: updates });
}
export function archiveBook(bookId) {
    return request(`/api/books/${bookId}`, { method: 'DELETE' });
}
export function importFromUpload(bookId, fileId) {
    return request(`/api/books/${bookId}/import-from-upload`, {
        method: 'POST',
        body: { file_id: fileId },
        timeoutMs: 120000,
    });
}

// ─── Chapters ───────────────────────────────────────────────────────
export function createChapter(bookId, payload) {
    return request(`/api/books/${bookId}/chapters`, { method: 'POST', body: payload });
}
export function getChapter(bookId, chapterId) {
    return request(`/api/books/${bookId}/chapters/${chapterId}`);
}
export function updateChapter(bookId, chapterId, payload) {
    return request(`/api/books/${bookId}/chapters/${chapterId}`, {
        method: 'PUT', body: payload,
    });
}
export function updateChapterStatus(bookId, chapterId, status) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/status`, {
        method: 'PATCH', body: { status },
    });
}
export function deleteChapter(bookId, chapterId) {
    return request(`/api/books/${bookId}/chapters/${chapterId}`, { method: 'DELETE' });
}
export function reorderChapters(bookId, orderedIds) {
    return request(`/api/books/${bookId}/chapters/reorder`, {
        method: 'PUT', body: { chapter_ids: orderedIds },
    });
}
export function listVersions(bookId, chapterId) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/versions`);
}
export function restoreVersion(bookId, chapterId, versionId) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/restore/${versionId}`, {
        method: 'POST',
    });
}

// S57 B.5 — envoi depuis bulle chat vers chapitre courant
export function appendToChapter(bookId, chapterId, content, { separator = '\n\n' } = {}) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/append`, {
        method: 'POST',
        body: { content, separator },
    });
}

// S57 C — révision LLM chapitre (6 modes) depuis atelier
export function workChapter(bookId, chapterId, { mode, instruction, section_title = null, position = null, target_passage = null, model = null }) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/work`, {
        method: 'POST',
        body: { mode, instruction, section_title, position, target_passage, model },
        timeoutMs: 120000,
    });
}

// S58 P3 — Style notes : annotations stylistiques typées par chapitre
export function listStyleNotes(bookId, chapterId) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/style-notes`);
}
export function createStyleNote(bookId, chapterId, { noteType, content }) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/style-notes`, {
        method: 'POST',
        body: { note_type: noteType, content },
    });
}
export function updateStyleNote(bookId, chapterId, noteId, patch) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/style-notes/${noteId}`, {
        method: 'PATCH',
        body: patch,
    });
}
export function deleteStyleNote(bookId, chapterId, noteId) {
    return request(`/api/books/${bookId}/chapters/${chapterId}/style-notes/${noteId}`, {
        method: 'DELETE',
    });
}

// S58 P1 — Transitions inter-chapitres : analyze (diagnostic + proposition) + apply (patchs)
export function analyzeTransition(bookId, fromChapterId, toChapterId, { modelTier = 'sonnet' } = {}) {
    return request(`/api/books/${bookId}/transitions/analyze`, {
        method: 'POST',
        body: {
            from_chapter_id: fromChapterId,
            to_chapter_id: toChapterId,
            model_tier: modelTier,
        },
        timeoutMs: 120000,
    });
}
export function applyTransition(bookId, fromChapterId, toChapterId, { closingAddition = null, openingModification = null } = {}) {
    return request(`/api/books/${bookId}/transitions/apply`, {
        method: 'POST',
        body: {
            from_chapter_id: fromChapterId,
            to_chapter_id: toChapterId,
            closing_addition: closingAddition,
            opening_modification: openingModification,
        },
        timeoutMs: 30000,
    });
}

// S57 D — Import intelligent : propose (IA classe) + apply (crée chapitres)
export function aiImportPropose(bookId, fileIds, { hint = '' } = {}) {
    return request(`/api/books/${bookId}/import-ai/propose`, {
        method: 'POST',
        body: { file_ids: fileIds, hint },
        timeoutMs: 120000,
    });
}
export function aiImportApply(bookId, mapping) {
    return request(`/api/books/${bookId}/import-ai/apply`, {
        method: 'POST',
        body: { mapping },
        timeoutMs: 60000,
    });
}

// ─── Auto-save debounce helper ──────────────────────────────────────
export function createAutoSaver({ bookId, chapterId, delayMs = 2000, onSaved, onError }) {
    let timer = null;
    let pending = null;
    let savingPromise = null;

    const flush = async () => {
        timer = null;
        if (pending === null) return;
        const content = pending;
        pending = null;
        try {
            savingPromise = updateChapter(bookId, chapterId, { content_text: content });
            const result = await savingPromise;
            savingPromise = null;
            if (onSaved) onSaved(result);
        } catch (err) {
            savingPromise = null;
            if (onError) onError(err);
        }
    };

    return {
        push(content) {
            pending = content;
            if (timer) clearTimeout(timer);
            timer = setTimeout(flush, delayMs);
        },
        async forceFlush() {
            if (timer) {
                clearTimeout(timer);
                await flush();
            }
            if (savingPromise) {
                try { await savingPromise; } catch {}
            }
        },
        cancel() {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            pending = null;
        },
    };
}
